import { Grid2, Stack } from '@mui/material';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ViewTab from '@components/ViewTab';

import { useIsMobile } from '@hooks/useIsMobile';
import { useSession } from '@hooks/useSession';

import type { HealthSummaryData } from '@models/patient-record';

import CareTeam from '../CareTeam';
import Contacts from '../Contacts';
import Directives from '../Directives';
import Summary from '../Summary';
import InsuranceSection from '../Summary/InsuranceSection';
import ProfileCard from '../Summary/ProfileCard';

const messages = defineMessages({
  summary: {
    id: 'healthSummary.summary',
    defaultMessage: 'medical',
  },
  directives: {
    id: 'healthSummary.directives',
    defaultMessage: 'directives',
  },
  contacts: {
    id: 'healthSummary.contacts',
    defaultMessage: 'contacts',
  },
  title: {
    id: 'healthSummary.title',
    defaultMessage: 'Health Summary',
  },
});

interface HealthSummaryContentProps {
  loading: boolean;
  data: HealthSummaryData;
  isSharing?: boolean;
}

export default function HealthSummaryContent({
  data,
  isSharing,
  loading,
}: HealthSummaryContentProps) {
  const { formatMessage } = useIntl();
  const {
    state: { activeProfile, isAuthenticated },
  } = useSession();
  const { care_team, legal_directives, related_persons, coverage, patient } =
    data;

  const tabs = [
    { id: '0', value: formatMessage(messages.summary) },
    { id: '1', value: formatMessage(messages.directives) },
    { id: '2', value: formatMessage(messages.contacts) },
  ];
  // eslint-disable-next-line react/hook-use-state
  const [activeTab, switchActiveTab] = useState(tabs[0]);
  const mobile = useIsMobile();

  const baseProps = {
    clickable: isAuthenticated,
    loading,
  };

  return (
    <>
      {mobile ? (
        <Stack justifyContent="center" m={2} spacing={3}>
          {isSharing || activeProfile ? (
            <>
              <ProfileCard
                isSharing={isSharing}
                loading={loading}
                patient={patient}
              />
              <InsuranceSection {...baseProps} coverage={coverage} />
            </>
          ) : null}

          <ViewTab
            changeTab={switchActiveTab}
            selected={activeTab}
            tabArray={tabs}
          />

          {activeTab.value === formatMessage(messages.summary) ? (
            <Summary {...baseProps} {...data} />
          ) : activeTab.value === formatMessage(messages.directives) ? (
            <Directives {...baseProps} legal_directives={legal_directives} />
          ) : (
            <>
              <Contacts {...baseProps} related_persons={related_persons} />
              <CareTeam {...baseProps} care_team={care_team} />
            </>
          )}
        </Stack>
      ) : (
        <Grid2 container mb={2} spacing={3}>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Stack spacing={2}>
              <ProfileCard
                isSharing={isSharing}
                loading={loading}
                patient={patient}
              />
              <InsuranceSection {...baseProps} coverage={coverage} />
              <CareTeam {...baseProps} care_team={care_team} />
            </Stack>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <Summary {...baseProps} {...data} />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <Stack spacing={2}>
              <Directives {...baseProps} legal_directives={legal_directives} />
              <Contacts {...baseProps} related_persons={related_persons} />
            </Stack>
          </Grid2>
        </Grid2>
      )}
    </>
  );
}
